import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { 
  Loader2, AlertCircle, Mail, Paperclip, Star, RefreshCw, 
  GripVertical, MoreHorizontal, ChevronDown, ChevronUp, 
  CheckCircle, Circle, Brain, Settings, Zap, Info, Sparkles, MessageSquare, X, Plus
} from 'lucide-react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { mailApi, todoApi, aiApi, subtasksApi, MailTodoLink, SmartAiColumn } from '../../api/client';
import { AlertTriangle } from 'lucide-react';
import { formatDate } from '../../utils/date';
import { cn } from '../../utils/cn';
import { SubtaskList } from './SubtaskList';

interface AiMatchDetail {
  messageId: string;
  confidence: number;
  reasoning: string | null;
}

interface AiKanbanColumnProps {
  column: SmartAiColumn;
  account: any;
  aiMatches: string[]; // Message IDs that match this column
  aiMatchDetails?: AiMatchDetail[]; // Full evaluation details with reasoning
  onSelectMail: (messageId: string) => void;
  selectedMessageId?: string;
  isFrozen?: boolean;
  isDragEnabled?: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  excludeMessageIds?: Set<string>;
  onMessagesLoaded?: (messageIds: string[]) => void;
  onEditColumn?: () => void;
  isScanning?: boolean;
  showSummary?: boolean; // Kerninhalt-Modus
  onToggleSummary?: () => void;
  viewMode?: 'board' | 'list';
}

// Popup component for AI reasoning
function ReasoningPopup({ confidence, reasoning }: { confidence?: number; reasoning?: string | null }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const confidenceColor = confidence && confidence >= 0.8 
    ? 'var(--color-success)' 
    : confidence && confidence >= 0.5
      ? 'rgb(168, 85, 247)'
      : 'var(--color-warning)';
  
  const confidenceBg = confidence && confidence >= 0.8 
    ? 'rgba(34, 197, 94, 0.15)' 
    : confidence && confidence >= 0.5
      ? 'rgba(168, 85, 247, 0.15)'
      : 'rgba(234, 179, 8, 0.15)';
  
  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium cursor-pointer transition-colors"
        style={{ backgroundColor: confidenceBg, color: confidenceColor }}
        title={reasoning ? 'Klicken für AI-Begründung' : 'AI-klassifiziert'}
      >
        <Brain className="w-2.5 h-2.5" />
        {confidence ? `${Math.round(confidence * 100)}%` : 'AI'}
        {reasoning && <MessageSquare className="w-2 h-2 ml-0.5" />}
      </button>
      
      {isOpen && reasoning && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          {/* Popup */}
          <div 
            className="absolute left-0 top-full mt-1 z-50 w-64 p-3 rounded-lg shadow-lg"
            style={{ 
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-default)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'rgb(168, 85, 247)' }}>
                <Brain className="w-3.5 h-3.5" />
                AI-Begründung
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />
              </button>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {reasoning}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default function AiKanbanColumn({ 
  column, 
  account, 
  aiMatches,
  aiMatchDetails = [],
  onSelectMail,
  selectedMessageId,
  isFrozen = false,
  isDragEnabled = true,
  onQuickAction,
  onAddTodo,
  excludeMessageIds,
  onMessagesLoaded,
  onEditColumn,
  isScanning = false,
  showSummary = false,
  onToggleSummary,
  viewMode = 'board',
}: AiKanbanColumnProps) {
  const listRef = useRef<HTMLDivElement>(null);
  
  // Create lookup map for evaluation details
  const matchDetailsMap = useMemo(() => {
    const map = new Map<string, AiMatchDetail>();
    aiMatchDetails.forEach(d => map.set(d.messageId, d));
    return map;
  }, [aiMatchDetails]);
  
  // Fetch actual mail data for matched message IDs using batch API
  // MS Graph $batch bundles up to 20 requests per HTTP call
  const { data: messagesData, isLoading, error, refetch } = useQuery({
    queryKey: ['aiColumnMessages', account?.id, column.id, aiMatches],
    queryFn: async () => {
      if (!account || aiMatches.length === 0) {
        return { messages: [], errors: [] };
      }
      
      // Use batch endpoint - much more efficient than parallel getMessage calls
      // For 50 messages: 3 HTTP calls instead of 50 (MS Graph batches 20 per request)
      const messagesToFetch = aiMatches.slice(0, 50);
      
      // Retry logic with exponential backoff for rate limits
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        try {
          const result = await mailApi.getMessagesBatch(account.id, messagesToFetch);
          return result;
        } catch (err: any) {
          // Check for rate limit (429) or server error (5xx)
          const status = err?.response?.status;
          if (status === 429 || (status >= 500 && status < 600)) {
            retries++;
            if (retries < maxRetries) {
              // Exponential backoff: 1s, 2s, 4s
              const delay = Math.pow(2, retries - 1) * 1000;
              console.warn(`[AiColumn] Rate limited, retry ${retries}/${maxRetries} in ${delay}ms`);
              await new Promise(r => setTimeout(r, delay));
              continue;
            }
          }
          throw err;
        }
      }
      
      return { messages: [], errors: ['Max retries exceeded'] };
    },
    enabled: !!account && aiMatches.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes cache - messages don't change often
    gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes
    retry: false, // We handle retries ourselves
    placeholderData: keepPreviousData,
  });
  
  const rawMessagesUnfiltered = messagesData?.messages || [];
  // Filter out messages that are no longer in the source folder (moved/deleted externally)
  const rawMessages = useMemo(() => {
    if (!column.baseSourceId || column.baseSourceType === 'all') return rawMessagesUnfiltered;
    if (column.baseSourceType === 'folder') {
      return rawMessagesUnfiltered.filter((m: any) => !m.folderId || m.folderId === column.baseSourceId);
    }
    return rawMessagesUnfiltered;
  }, [rawMessagesUnfiltered, column.baseSourceId, column.baseSourceType]);
  const messageIds = useMemo(() => rawMessages.map((m: any) => m.id), [rawMessages]);
  
  // Fetch todo links
  const { data: todoLinksData } = useQuery({
    queryKey: ['mailTodoLinks', account?.id, messageIds],
    queryFn: () => {
      if (!account || messageIds.length === 0) return { links: {} };
      return todoApi.getMailLinksBatch(account.id, messageIds);
    },
    enabled: !!account && messageIds.length > 0,
    staleTime: 10 * 1000, // Reduced: 10 seconds cache
    refetchInterval: 15 * 1000, // Sync every 15 seconds
  });
  
  const todoLinks = todoLinksData?.links || {};
  
  // Fetch cached AI summaries in PARALLEL with messages (using aiMatches IDs directly)
  // This eliminates the waterfall: summaries don't wait for MS/Google API anymore
  const { data: cachedSummariesData } = useQuery({
    queryKey: ['mailSummariesCached', account?.id, aiMatches, showSummary],
    queryFn: async () => {
      if (!account || aiMatches.length === 0) return { summaries: {} };
      return mailApi.getSummaries(account.id, aiMatches.slice(0, 50));
    },
    enabled: !!account && aiMatches.length > 0 && showSummary,
    staleTime: 15 * 60 * 1000, // 15 min - summaries are persisted in DB
  });

  // Generate missing summaries AFTER messages are loaded (needs bodyPreview etc.)
  const { data: generatedSummariesData } = useQuery({
    queryKey: ['mailSummariesGenerate', account?.id, messageIds, cachedSummariesData],
    queryFn: async () => {
      if (!account || messageIds.length === 0 || !cachedSummariesData) return { summaries: {} };
      const cached = cachedSummariesData.summaries || {};
      const missing = messageIds.filter((id: string) => !cached[id]);
      
      if (missing.length > 0 && missing.length <= 10) {
        const toGenerate = rawMessages
          .filter((m: any) => missing.includes(m.id))
          .slice(0, 10)
          .map((m: any) => ({
            messageId: m.id,
            subject: m.subject || '',
            bodyPreview: m.bodyPreview || '',
            from: m.from?.name || m.from?.email || '',
          }));
        
        if (toGenerate.length > 0) {
          return mailApi.generateSummariesBatch(account.id, toGenerate);
        }
      }
      
      return { summaries: {} };
    },
    enabled: !!account && messageIds.length > 0 && showSummary && !!cachedSummariesData,
    staleTime: 15 * 60 * 1000,
  });

  const isSummariesLoading = !cachedSummariesData && showSummary && aiMatches.length > 0;

  // Merge cached + generated summaries
  const summariesData = useMemo(() => ({
    summaries: {
      ...(cachedSummariesData?.summaries || {}),
      ...(generatedSummariesData?.summaries || {}),
    }
  }), [cachedSummariesData, generatedSummariesData]);
  
  const summaries = summariesData?.summaries || {};
  
  // Fetch cost status
  const queryClient = useQueryClient();
  const { data: costStatusData } = useQuery({
    queryKey: ['aiColumnCostStatus', column.id],
    queryFn: () => aiApi.getAiColumnCostStatus(column.id),
    enabled: !!column.id,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refresh every minute
  });
  
  const costStatus = costStatusData?.status;
  const isLimitExceeded = costStatus?.tokenLimitExceeded || costStatus?.evalLimitExceeded;
  
  // Boost limit mutation
  const boostLimitMutation = useMutation({
    mutationFn: (newLimit: number) => aiApi.boostAiColumnLimit(column.id, newLimit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiColumnCostStatus', column.id] });
    },
  });
  
  const handleBoostLimit = () => {
    // Double the current limit or set to 1M, whichever is higher
    const currentLimit = costStatus?.maxTokensPerDay || 500000;
    const newLimit = Math.max(currentLimit * 2, 1000000);
    boostLimitMutation.mutate(newLimit);
  };
  
  // Filter out duplicates, stale messages (no longer in source folder), and sort by date (newest first)
  const messages = useMemo(() => {
    let filtered = rawMessages;
    
    // Filter out messages that are no longer in the AI column's source folder
    // (e.g. archived, deleted, or moved externally in Outlook/Gmail)
    if (column.baseSourceType === 'folder' && column.baseSourceId && column.baseSourceId !== 'all') {
      filtered = filtered.filter((m: any) => !m.folderId || m.folderId === column.baseSourceId);
    }
    
    if (excludeMessageIds && excludeMessageIds.size > 0) {
      filtered = filtered.filter((m: any) => !excludeMessageIds.has(m.id));
    }
    
    // Sort by receivedAt descending (newest first)
    filtered = [...filtered].sort((a: any, b: any) => {
      const dateA = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const dateB = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return dateB - dateA;
    });
    
    return filtered;
  }, [rawMessages, excludeMessageIds, column.baseSourceId, column.baseSourceType]);
  
  // Report loaded message IDs
  useEffect(() => {
    if (onMessagesLoaded && aiMatches.length > 0) {
      onMessagesLoaded(aiMatches);
    }
  }, [aiMatches, onMessagesLoaded]);
  
  // Drop target
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `ai-column-${column.id}`,
    data: {
      type: 'ai-column',
      column,
      accountId: account?.id,
    },
  });
  
  // Hide if empty and not enabled
  if (!column.enabled) {
    return null;
  }
  
  return (
    <div 
      ref={setDropRef}
      className={cn(
        cn('flex flex-col overflow-hidden transition-all', viewMode === 'list' ? 'flex-1 min-h-0' : 'flex-shrink-0 rounded-xl shadow-theme'),
        viewMode === 'list' ? 'w-full' : 'w-80',
        isOver && 'ring-2 ring-primary ring-offset-2'
      )}
      style={{
        backgroundColor: isOver ? 'var(--bg-hover)' : 'var(--bg-primary)',
        border: `1px solid ${isOver ? 'var(--brand-primary)' : 'var(--border-default)'}`,
        maxHeight: viewMode === 'list' ? '100%' : 'calc(100vh - 180px)',
        minHeight: '200px',
        height: viewMode === 'list' ? '100%' : 'fit-content',
        ...(isFrozen ? {
          position: 'sticky',
          left: 0,
          zIndex: 10,
          boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
        } : {}),
      }}
    >
      {/* Column Header */}
      <div 
        className="flex items-center justify-between px-4 py-3"
        style={{
          borderBottom: '1px solid var(--border-default)',
          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(168, 85, 247, 0.15))',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{column.icon || '🧠'}</span>
          <h3 
            className="font-semibold flex items-center gap-1.5"
            style={{ color: 'var(--text-primary)' }}
          >
            {column.name}
            <Brain className="w-3 h-3" style={{ color: 'rgb(168, 85, 247)' }} />
          </h3>
          <span 
            className="text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
            style={{ 
              backgroundColor: 'rgba(168, 85, 247, 0.15)',
              color: 'rgb(168, 85, 247)',
            }}
          >
            {isScanning ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Scannt...
              </>
            ) : (
              messages.length
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Kerninhalt Toggle */}
          {onToggleSummary && (
            <button 
              onClick={onToggleSummary}
              className={cn(
                "p-1.5 rounded-lg transition-colors flex items-center gap-1",
                showSummary && "bg-purple-500/20"
              )}
              style={{ color: showSummary ? 'rgb(168, 85, 247)' : 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = showSummary ? 'rgba(168, 85, 247, 0.3)' : 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = showSummary ? 'rgba(168, 85, 247, 0.2)' : 'transparent'}
              title={showSummary ? 'Kerninhalt aktiv' : 'Kerninhalt aktivieren'}
            >
              <Brain className="w-4 h-4" />
              {isSummariesLoading && showSummary && <Loader2 className="w-3 h-3 animate-spin" />}
            </button>
          )}
          {/* Confidence indicator */}
          <div 
            className="px-2 py-1 rounded text-[10px] font-medium"
            style={{ 
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              color: 'var(--color-success)',
            }}
            title="AI-gesteuerte Spalte"
          >
            <Zap className="w-3 h-3 inline-block mr-1" />
            AI
          </div>
          {onEditColumn && (
            <button 
              onClick={onEditColumn}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Spalte bearbeiten"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          <button 
            onClick={() => refetch()}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Prompt hint */}
      <div 
        className="px-3 py-2 text-xs flex items-start gap-2"
        style={{ 
          backgroundColor: 'rgba(168, 85, 247, 0.05)',
          borderBottom: '1px solid var(--border-default)',
        }}
      >
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--text-tertiary)' }} />
        <span style={{ color: 'var(--text-tertiary)' }} className="line-clamp-2">
          {column.prompt}
        </span>
      </div>
      
      {/* Limit exceeded warning */}
      {isLimitExceeded && costStatus && (
        <div 
          className="px-3 py-2 text-xs"
          style={{ 
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-error)' }} />
            <div className="flex-1">
              <p className="font-medium" style={{ color: 'var(--color-error)' }}>
                {costStatus.tokenLimitExceeded 
                  ? `Token-Limit erreicht (${(costStatus.tokensUsedToday / 1000).toFixed(0)}k / ${(costStatus.maxTokensPerDay / 1000).toFixed(0)}k)`
                  : `Evaluierungs-Limit erreicht (${costStatus.evalsLastHour} / ${costStatus.maxEvalsPerHour} pro Stunde)`
                }
              </p>
              <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
                AI-Evaluierungen sind pausiert bis das Limit zurückgesetzt wird.
              </p>
              <button
                onClick={handleBoostLimit}
                disabled={boostLimitMutation.isPending}
                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                style={{
                  backgroundColor: 'rgba(168, 85, 247, 0.15)',
                  color: 'rgb(168, 85, 247)',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.25)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.15)'}
              >
                {boostLimitMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Zap className="w-3 h-3" />
                )}
                Limit auf {((costStatus.maxTokensPerDay * 2) / 1000).toFixed(0)}k erhöhen
              </button>
              {boostLimitMutation.isSuccess && (
                <p className="mt-1.5 text-xs" style={{ color: 'var(--color-success)' }}>
                  ✓ Limit erhöht! Seite neu laden um fortzufahren.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Messages List */}
      <div 
        ref={listRef}
        className={cn(
          'flex-1 overflow-y-auto',
          viewMode === 'list' ? 'p-0' : 'p-2 space-y-2'
        )}
      >
        {isLoading && !messages.length ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderLeft: '3px solid rgb(168, 85, 247)', padding: '0.75rem' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="h-3.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '40%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
                <div className="h-3.5 rounded mb-2" style={{ backgroundColor: 'var(--bg-tertiary)', width: '80%' }} />
                <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '60%' }} />
                <div className="flex gap-2 mt-3">
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '20%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-5 h-5 text-error mb-2" />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Fehler beim Laden</p>
            <button 
              onClick={() => refetch()}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        ) : aiMatches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Brain className="w-8 h-8 mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Keine Mails gefunden
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-disabled)' }}>
              AI hat noch keine passenden Mails identifiziert
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin mb-2" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Lade {aiMatches.length} Mails...
            </p>
          </div>
        ) : (
          messages.map((message: any) => (
            <AiMailCard
              key={message.id}
              message={message}
              accountId={account?.id}
              columnId={column.id}
              isSelected={selectedMessageId === message.id}
              onClick={() => onSelectMail(message.id)}
              isDragEnabled={isDragEnabled}
              onQuickAction={onQuickAction}
              onAddTodo={onAddTodo}
              todoLink={todoLinks[message.id]}
              provider={account?.provider || 'microsoft'}
              aiDetail={matchDetailsMap.get(message.id)}
              summary={showSummary ? summaries[message.id] : undefined}
              viewMode={viewMode}
            />
          ))
        )}
      </div>
    </div>
  );
}

// AI Mail Card with confidence badge and reasoning - same design as DraggableMailCard
function AiMailCard({ 
  message, 
  accountId, 
  columnId, 
  isSelected, 
  onClick,
  isDragEnabled,
  onQuickAction,
  onAddTodo,
  todoLink,
  provider,
  aiDetail,
  summary,
  viewMode = 'board',
}: {
  message: any;
  accountId: string;
  columnId: string;
  isSelected: boolean;
  onClick: () => void;
  aiDetail?: AiMatchDetail;
  isDragEnabled: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  todoLink?: MailTodoLink;
  provider: string;
  summary?: string;
  viewMode?: 'board' | 'list';
}) {
  const [showTasks, setShowTasks] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch subtasks count to determine if we should auto-expand
  const { data: subtasksData } = useQuery({
    queryKey: ['subtasks', accountId, message.id],
    queryFn: () => subtasksApi.getSubtasks(accountId, message.id),
    enabled: provider === 'microsoft' && !!todoLink,
    staleTime: 10 * 1000,
  });
  
  const subtasksCount = subtasksData?.subtasks?.length || 0;
  
  // Auto-expand only when there are actual subtasks
  useEffect(() => {
    if (todoLink && !hasAutoExpanded && subtasksCount > 0) {
      setShowTasks(true);
      setHasAutoExpanded(true);
    }
  }, [todoLink, hasAutoExpanded, subtasksCount]);
  
  // Toggle todo completion mutation
  const toggleTodoMutation = useMutation({
    mutationFn: () => todoApi.toggleMailTodo(accountId, message.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailTodoLink', accountId, message.id] });
      queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
    },
  });
  
  const handleToggleTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleTodoMutation.mutate();
  };
  
  const handleAddTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddTodo) {
      onAddTodo({ message, accountId });
    }
  };
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `ai-mail-${message.id}`,
    data: {
      type: 'mail',
      message,
      accountId,
      columnId,
    },
    disabled: !isDragEnabled,
  });
  
  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: 1000,
    opacity: isDragging ? 0.8 : 1,
  } : undefined;
  
  // ===== LIST MODE: Compact Outlook/Gmail-style row =====
  if (viewMode === 'list') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'w-full text-left transition-colors cursor-pointer',
          isSelected && 'ring-1 ring-primary',
          isDragging && 'opacity-60'
        )}
        onClick={onClick}
        {...attributes}
      >
        <div 
          className="flex items-start gap-1"
          style={{
            backgroundColor: isSelected ? 'var(--bg-hover)' : 'transparent',
            borderBottom: '1px solid var(--border-default)',
            borderLeft: '3px solid rgb(168, 85, 247)',
            padding: '0.375rem 0.75rem',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {isDragEnabled && (
            <div 
              {...listeners}
              className="hidden sm:flex flex-shrink-0 items-center pt-1 cursor-grab active:cursor-grabbing opacity-0 hover:opacity-100 transition-opacity"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-3 h-3" style={{ color: 'var(--text-disabled)' }} />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className={cn('text-sm truncate', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
                {message.from?.name || message.from?.email || 'Unbekannt'}
              </span>
              <span className="text-xs whitespace-nowrap flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                {formatDate(message.receivedAt)}
              </span>
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className={cn('text-sm truncate flex-1 min-w-0', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
                {message.subject}
              </span>
              {message.hasAttachments && <Paperclip className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />}
              {message.importance === 'high' && <Star className="w-3 h-3 flex-shrink-0 text-warning fill-warning" />}
              {todoLink && (
                <button
                  onClick={handleToggleTodo}
                  disabled={toggleTodoMutation.isPending}
                  className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0 rounded font-medium flex-shrink-0"
                  style={{
                    backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                    color: todoLink.completed ? 'var(--color-success)' : 'var(--color-warning)',
                  }}
                >
                  {todoLink.completed ? <CheckCircle className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                </button>
              )}
              <ReasoningPopup confidence={aiDetail?.confidence} reasoning={aiDetail?.reasoning} />
              {message.categories?.slice(0, 1).map((cat: string, i: number) => (
                <span key={i} className="text-[10px] px-1 py-0 rounded font-medium flex-shrink-0 truncate max-w-[80px]" style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--brand-primary)' }}>
                  {cat}
                </span>
              ))}
            </div>
            
            {(summary || message.bodyPreview) && (
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                {summary || message.bodyPreview}
              </p>
            )}
          </div>
          
          {onQuickAction && (
            <button
              className="flex-shrink-0 p-1 rounded transition-colors opacity-0 hover:opacity-100"
              style={{ color: 'var(--text-tertiary)' }}
              onClick={e => { e.stopPropagation(); onQuickAction({ message, accountId, columnId }); }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.opacity = '0'; }}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ===== BOARD MODE: Original card layout =====
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'w-full text-left p-3 rounded-lg transition-all cursor-pointer',
        isSelected && 'ring-2 ring-primary',
        isDragging && 'shadow-2xl'
      )}
      onClick={onClick}
      {...attributes}
    >
      <div 
        className="flex items-start gap-2"
        style={{
          backgroundColor: isSelected 
            ? 'var(--bg-hover)' 
            : message.isRead 
              ? 'var(--bg-primary)' 
              : 'rgba(168, 85, 247, 0.08)',
          border: `1px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-default)'}`,
          borderLeft: '3px solid rgb(168, 85, 247)',
          borderRadius: '0.5rem',
          padding: '0.75rem',
        }}
      >
        {isDragEnabled && (
          <div className="flex-shrink-0 flex flex-col gap-1 -ml-1">
            <div 
              {...listeners}
              className="hidden sm:block cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
            </div>
            {onQuickAction && (
              <button
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onClick={e => {
                  e.stopPropagation();
                  onQuickAction({ message, accountId, columnId });
                }}
                onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onTouchEnd={e => e.currentTarget.style.backgroundColor = 'transparent'}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Verschieben..."
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span 
              className={cn('text-sm truncate', !message.isRead && 'font-semibold')}
              style={{ color: 'var(--text-primary)' }}
            >
              {message.from?.name || message.from?.email || 'Unbekannt'}
            </span>
            <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
              {formatDate(message.receivedAt)}
            </span>
          </div>
          
          <p className={cn('text-sm truncate mb-1', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
            {message.subject}
          </p>
          
          <div style={{ minHeight: '2.5rem' }}>
            {summary && summary.trim() ? (
              <div className="flex items-start gap-1.5 text-xs break-all" style={{ color: 'var(--text-secondary)' }}>
                <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(168, 85, 247)' }} />
                <span className="italic">{summary}</span>
              </div>
            ) : message.bodyPreview ? (
              <p className="text-xs break-all" style={{ color: 'var(--text-tertiary)' }}>{message.bodyPreview}</p>
            ) : null}
          </div>
          
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {message.hasAttachments && <Paperclip className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />}
            {message.importance === 'high' && <Star className="w-3 h-3 text-warning fill-warning" />}
            {todoLink ? (
              <button
                onClick={handleToggleTodo}
                disabled={toggleTodoMutation.isPending}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                  color: todoLink.completed ? 'var(--color-success)' : 'var(--color-warning)',
                  cursor: toggleTodoMutation.isPending ? 'wait' : 'pointer',
                }}
              >
                {toggleTodoMutation.isPending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : todoLink.completed ? <CheckCircle className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                {todoLink.completed ? '✓ Erledigt' : 'Offen'}
              </button>
            ) : onAddTodo && (
              <button
                onClick={handleAddTodo}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: 'var(--brand-primary)', border: '1px dashed var(--brand-primary)' }}
              >
                <Plus className="w-2.5 h-2.5" />
                Aufgabe
              </button>
            )}
            <ReasoningPopup confidence={aiDetail?.confidence} reasoning={aiDetail?.reasoning} />
            {message.categories?.slice(0, 1).map((cat: string, i: number) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--brand-primary)' }}>
                {cat}
              </span>
            ))}
          </div>
          
          {todoLink && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowTasks(!showTasks); }}
              className="flex items-center gap-1 mt-2 text-xs transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
            >
              {showTasks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span>{showTasks ? 'Subtasks ausblenden' : 'Subtasks anzeigen'}</span>
            </button>
          )}
        </div>
      </div>
      
      {showTasks && todoLink && (
        <div 
          className="mt-3 ml-1 pl-3 py-2 rounded-r-lg"
          style={{ borderLeft: '2px solid var(--brand-primary)', backgroundColor: 'var(--bg-secondary)' }}
          onClick={e => e.stopPropagation()}
        >
          <SubtaskList accountId={accountId} messageId={message.id} provider={provider} todoLinkExists={!!todoLink} />
        </div>
      )}
    </div>
  );
}
